import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcDtccCnsV1AssetDayQueryServiceAssetDayGet } from '@interxfi/api-client/generated/hooks';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { DateFormat } from '@interxfi/design-system/components/format/date';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcDtccCnsV1AssetDayState } from '@interxfi/types/generated/api';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { AssetBreadcrumb } from '../../../../../../../../../../../features/asset/asset-breadcrumb';
import { AssetLogoBreadcrumb } from '../../../../../../../../../../../features/asset/logo/asset-logo-breadcrumb';
import { CnsAssetDayEntityMenu } from '../../../../../../../../../../../features/dtcc/cns/asset-day/cns-asset-day-entity-menu';
import { CnsAssetDayEventSummary } from '../../../../../../../../../../../features/dtcc/cns/asset-day/cns-asset-day-event';
import { getCnsAssetDayStateListItems } from '../../../../../../../../../../../features/dtcc/cns/asset-day/cns-asset-day-list-items';
import { getCnsAssetDayDetailPath } from '../../../../../../../../../../../features/dtcc/cns/asset-day/cns-asset-day-routing';
import { CnsAssetDayStatusPill } from '../../../../../../../../../../../features/dtcc/cns/asset-day/cns-asset-day-status-pill';
import { CnsCaPaymentsTable } from '../../../../../../../../../../../features/dtcc/cns/asset-day/cns-ca-payments-table';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/dtcc/asset-day/_asset-day/$assetId/$date',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `cns-asset-day-${params.assetId}-${params.date}`,
        label: (
          <HorizontalStack blockAlign="center" gap={1} wrap="nowrap">
            <AssetBreadcrumb assetId={params.assetId} />
            <span>
              (<DateFormat value={params.date} wellKnownType="date_iso" />)
            </span>
          </HorizontalStack>
        ),
        prefix: <AssetLogoBreadcrumb assetId={params.assetId} />,
        to: getCnsAssetDayDetailPath(params.assetId, params.date),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('CNS Asset Day'),
  component: CnsAssetDayDetail,
});

function getListItems(data: IxcDtccCnsV1AssetDayState | undefined) {
  const items = getCnsAssetDayStateListItems(data);

  return {
    left: [
      {
        id: 'main',
        items: [items.data.security.cusip, items.data.security.name, items.data.security.symbol.formatted, items.data.next, items.data.previous],
      },
      {
        id: 'cnsMessage',
        title: 'CNS Message',
        items: [
          items.data.cnsMessage.marketValue,
          items.data.cnsMessage.netClosingPosition,
          items.data.cnsMessage.netDepositorySettlements,
          items.data.cnsMessage.netSettlingTrades,
          items.data.cnsMessage.price,
          items.data.cnsMessage.stockDividendsAndMisc,
        ],
      },
      {
        id: 'tradeTotals',
        title: 'Trade Totals',
        items: [
          items.data.tradeTotals.buyQuantity,
          items.data.tradeTotals.buyValue,
          items.data.tradeTotals.sellQuantity,
          items.data.tradeTotals.sellValue,
        ],
      },
      ...(items.data.opening.openingFail
        ? [
            {
              id: 'dayOpeningFail',
              title: 'Opening Fail',
              items: [
                items.data.opening.openingFail.quantity,
                items.data.opening.openingFail.tradeValue,
                items.data.opening.openingFail.marketValue,
                items.data.opening.openingFail.daysOpen,
              ],
            },
          ]
        : []),
      ...(data?.data.reconciledTotals
        ? [
            {
              id: 'reconciledTotals',
              title: 'Reconciled Totals',
              items: [
                items.data.reconciledTotals.netSettlingQuantity,
                items.data.reconciledTotals.stockDividendsAndMiscQuantity,
                ...(items.data.reconciledTotals.netTrade
                  ? [
                      { ...items.data.reconciledTotals.netTrade.quantity, title: 'Net Trade Quantity' },
                      { ...items.data.reconciledTotals.netTrade.value, title: 'Net Trade Value' },
                    ]
                  : []),
                ...(items.data.reconciledTotals.settlingTrade
                  ? [
                      { ...items.data.reconciledTotals.settlingTrade.quantity, title: 'Settling Trade Quantity' },
                      { ...items.data.reconciledTotals.settlingTrade.value, title: 'Settling Trade Value' },
                    ]
                  : []),
                ...(items.data.reconciledTotals.closingFail
                  ? [
                      { ...items.data.reconciledTotals.closingFail.quantity, title: 'Closing Fail Quantity' },
                      { ...items.data.reconciledTotals.closingFail.tradeValue, title: 'Closing Fail Trade Value' },
                      { ...items.data.reconciledTotals.closingFail.marketValue, title: 'Closing Fail Market Value' },
                      { ...items.data.reconciledTotals.closingFail.daysOpen, title: 'Closing Fail Days Open' },
                    ]
                  : []),
                ...(items.data.reconciledTotals.openingFail
                  ? [
                      { ...items.data.reconciledTotals.openingFail.quantity, title: 'Opening Fail Quantity' },
                      { ...items.data.reconciledTotals.openingFail.tradeValue, title: 'Opening Fail Trade Value' },
                      { ...items.data.reconciledTotals.openingFail.marketValue, title: 'Opening Fail Market Value' },
                      { ...items.data.reconciledTotals.openingFail.daysOpen, title: 'Opening Fail Days Open' },
                    ]
                  : []),
              ],
            },
          ]
        : []),
    ] as const satisfies ReadonlyListGroup[],
    right: [items.assetId, items.date] as const satisfies ReadonlyListItem[],
  } as const;
}

function CnsAssetDayDetail() {
  const { assetId, date } = Route.useParams();
  const request = { assetId, date } as const;
  const { data, isPending, error } = useIxcDtccCnsV1AssetDayQueryServiceAssetDayGet({ assetId, date });
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.assetDay), [data?.assetDay]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <CnsAssetDayStatusPill isPending={showPending} size="large" status={data?.assetDay?.status} />
          </HorizontalStack>

          <CnsAssetDayEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <CnsAssetDayEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>CA Payments</Typography.Level3>
          <CnsCaPaymentsTable payments={data?.assetDay?.data?.caPayments || STABLE_EMPTY_ARRAY_REF} showSkeleton={showPending} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
